import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import ConfirmModal from '../components/common/ConfirmModal';

const ConfirmContext = createContext(null);

const DEFAULTS = {
  title: 'Are you sure?',
  message: '',
  confirmText: 'Confirm',
  cancelText: 'Cancel',
  variant: 'primary',
};

export const ConfirmProvider = ({ children }) => {
  const [state, setState] = useState({ open: false, options: DEFAULTS, loading: false });
  const resolverRef = useRef(null);

  const confirm = useCallback((options = {}) => {
    return new Promise((resolve) => {
      // If another confirm() is still pending (caller never resolved it
      // before opening another), settle the previous one as false so the
      // awaiting code path doesn't hang forever.
      if (resolverRef.current) {
        try { resolverRef.current(false); } catch (_) { /* ignore */ }
      }
      resolverRef.current = resolve;
      setState({
        open: true,
        loading: false,
        options: { ...DEFAULTS, ...options },
      });
    });
  }, []);

  const handleClose = useCallback(() => {
    setState((s) => ({ ...s, open: false }));
    if (resolverRef.current) {
      resolverRef.current(false);
      resolverRef.current = null;
    }
  }, []);

  const handleConfirm = useCallback(() => {
    setState((s) => ({ ...s, open: false }));
    if (resolverRef.current) {
      resolverRef.current(true);
      resolverRef.current = null;
    }
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <ConfirmModal
        show={state.open}
        onHide={handleClose}
        onConfirm={handleConfirm}
        loading={state.loading}
        {...state.options}
      />
    </ConfirmContext.Provider>
  );
};

export const useConfirm = () => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    // Fallback to native confirm so pages don't crash if provider is missing.
    return (options = {}) => Promise.resolve(window.confirm(options.message || options.title || 'Are you sure?'));
  }
  return ctx;
};

export default ConfirmContext;
